// Layout and text primitives for dashboard surfaces, built to the 2026
// Figma (file hYHM1jX4YwTQ42r5T82zK2, nodes 7099:6232 / 7105:2495 / 7107:3146).
//
// Everything a panel needs to lay itself out lives here, so a section file
// contains data and composition and no CSS. Where the Vital City v2 system
// already has an equivalent (SectionCard, StatRow, Chip) this deliberately
// does NOT redefine it - import those from v2Styled. What follows is only
// what the new frames add.
import styled, { css } from "styled-components";
import {
  FONT, FIG_INK, FIG_MUTED, PANEL_BORDER, CARD_BORDER, HAIRLINE, TRACK,
  SHADOW_CARD, SHADOW_PANEL, SHADOW_CTA, RADIUS_PANEL, RADIUS_CTA,
  CTA_GRADIENT, PRIMARY, SUBTLE,
} from "./tokens";

/* ── page scaffolding ──────────────────────────────────────────────── */

// The tinted page body the whole dashboard sits on (Figma 7099:6234).
/* The summary content sits directly on the project page - Figma 7099:6232
   has no card around this region, and the Insight/Project Details toggle
   above shares the stat cards' left edge (both x=304 in the frame). Giving
   this a border and an inset produced a visible card-inside-a-card and
   pushed the cards out of line with the toggle (user 2026-08-24). */
export const DashShell = styled.div`
  font-family: ${FONT};
  color: ${FIG_INK};
`;

// A white content panel. $pad=false for panels that manage their own inset.
/* No entrance on panels (user 2026-08-21: "why u added fade up cards i
   dont want that") - motion lives in the numbers, bars and charts, exactly
   like the Gen Z home and Vital City. */
export const Panel = styled.section`
  background: #fff;
  border: 1px solid ${PANEL_BORDER};
  border-radius: ${RADIUS_PANEL}px;
  box-shadow: ${SHADOW_PANEL};
  /* Figma 7102:10799 -> 7102:10800: panel x=304, title x=337 = 33px inset. */
  padding: ${(p) => (p.$pad === false ? "0" : "20px 32px 24px")};
  margin-bottom: 20px;
  overflow: hidden;
`;

export const PanelHead = styled.header`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  /* The control stays on the title's line while there is room - it used to
     drop to its own row with empty space beside the subtitle (user
     2026-08-24). The text column shrinks first; only under 700px do they
     stack. */
  flex-wrap: nowrap;
  margin-bottom: ${(p) => (p.$tight ? "10px" : "18px")};
  > div:first-child {
    min-width: 0;
    flex: 1 1 auto;
  }
  > *:not(:first-child) { flex: 0 0 auto; }
  @media (max-width: 700px) {
    flex-wrap: wrap;
    > *:not(:first-child) { width: 100%; }
  }
`;

export const PanelTitle = styled.h2`
  font-family: ${FONT};
  font-size: 16px;
  font-weight: 600;
  line-height: 28px;
  color: ${FIG_INK};
  margin: 0;
  @media (max-width: 1100px) { font-size: 15px; line-height: 24px; }
  @media (max-width: 700px) { font-size: 14px; line-height: 22px; }
`;

export const PanelNote = styled.p`
  font-family: ${FONT};
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: ${FIG_MUTED};
  margin: 2px 0 0;
  max-width: 620px;
  @media (max-width: 1100px) { font-size: 13px; line-height: 19px; }
  @media (max-width: 700px) { font-size: 12px; line-height: 18px; }
`;

// The small print under a panel, separated by a hairline (Figma 7102:10963).
export const PanelFoot = styled.footer`
  font-family: ${FONT};
  font-size: 12px;
  line-height: 18px;
  color: ${FIG_MUTED};
  border-top: 1px solid ${HAIRLINE};
  margin-top: 18px;
  padding-top: 14px;
  b { color: ${FIG_INK}; font-weight: 700; }
`;

// Uppercase micro-label above a chart.
export const Kicker = styled.div`
  font-family: ${FONT};
  /* 11px in an 18px line box: at 10px with no line-height set, the browser
     line box was shorter than the cap height and shaved the tops off the
     capitals (user 2026-08-24). This is the shared chart-caption label, so
     the clipping showed up on every chart that used it. */
  font-size: 11px;
  font-weight: 700;
  line-height: 18px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${SUBTLE};
  margin-bottom: 8px;
`;

/* ── grids ─────────────────────────────────────────────────────────── */

// Panels side by side, stacking on narrow screens. $cols overrides.
export const PanelGrid = styled.div`
  display: grid;
  grid-template-columns: ${(p) => p.$cols || "1fr 1fr"};
  gap: 20px;
  align-items: start;
  @media (max-width: 991.99px) {
    grid-template-columns: 1fr;
  }
`;

// Chart on the left, written read on the right (the qual layer).
export const WithRead = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) ${(p) => p.$w || "300px"};
  gap: 18px;
  align-items: start;
  @media (max-width: 1199.99px) {
    grid-template-columns: 1fr;
  }
`;

/* ── stat cards ────────────────────────────────────────────────────── */

export const StatRow = styled.div`
  display: grid;
  grid-template-columns: repeat(${(p) => p.$n || 4}, 1fr);
  gap: 20px;
  margin-bottom: 20px;
  @media (max-width: 991.99px) { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 575.99px) { grid-template-columns: 1fr; }
`;

// The stat tile, matched 1:1 to the Gen Z dashboard's GZRTile family
// (user 2026-08-21: the 4 cards must be the SAME as Gen Z / Vital City):
// same chrome tokens, same arc mark asset (rendered by StatCards), same
// Inter label/sub, same gradient value with nowrap ellipsis + tooltip.
export const StatCard = styled.div`
  position: relative;
  background: #fff;
  border: 1px solid ${CARD_BORDER};
  border-radius: 20px;
  box-shadow: ${SHADOW_CARD};
  /* Figma 7101:6470: 90 tall, label inset 23/16, value baseline at 41. */
  padding: 16px 23px 18px;
  min-height: 90px;
  overflow: hidden;
  font-family: ${FONT};

  /* Figma 7101:6471 + 7101:6473: two thin #2E45DA ellipses at 14% opacity,
     rotated 46.03deg and skewed 2.06deg, sweeping through the right edge.
     Centres come straight from the frame (x 93% / 123% of a 256px card);
     the card's overflow clips them exactly as the frame does. */
  &::before,
  &::after {
    content: "";
    position: absolute;
    height: 318.321px;
    border-radius: 50%;
    /* A whisper, not a stripe. Figma layers a white 10% wash (7101:6474)
       over these before the text, and the Gen Z tile arc bakes a blur into
       its asset - the same treatment, done here with a low alpha plus a
       real blur so the edges never read as hard diagonal bars
       (user 2026-08-24). */
    background: rgba(46, 69, 218, 0.09);
    filter: blur(7px);
    transform: rotate(46.03deg) skewX(2.06deg);
    pointer-events: none;
  }
  /* Anchored to the RIGHT edge, not a percentage: the arcs hug the corner
     in Figma, so on a card wider than the frame's 256px a percentage push
     the second one clean off and only one shows (user 2026-08-24).
     Frame centres are 17.9px inside the right edge and 59px past it;
     the right offset is that distance minus half the ellipse width. */
  &::before {
    width: 13.165px;
    right: 11.34px;
    top: -176.88px;
  }
  &::after {
    width: 18.807px;
    right: -68.36px;
    top: -161.54px;
  }

  .stat-label {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    font-family: ${FONT};
    font-size: 14px;
    font-weight: 500;
    line-height: 18px;
    color: ${FIG_MUTED};
    position: relative;
    z-index: 1;
    /* ONE line, like Figma 7101:6476 - this is what holds the card at its
       90px height and keeps all four values on the same baseline. The text
       is a ClampText, so it shows its full self on hover only when cut. */
    min-height: 18px;
    margin-bottom: 7px;
  }
  .stat-label-text {
    flex: 1 1 auto;
    min-width: 0;
  }
  .stat-info {
    width: 14px;
    height: 14px;
    flex: none;
    cursor: help;
    margin-top: 2px;
  }
  .stat-label .anticon {
    color: ${FIG_MUTED};
    font-size: 13px;
    cursor: help;
  }
  .stat-body {
    /* One line, always: the caption sits beside the value and the value
       ellipsizes if it is long (Figma 7101:6475/7102:10706). Wrapping the
       caption to a second line grew the card past its 90px height and
       broke the row. Both carry their full text on hover. */
    display: flex;
    align-items: baseline;
    gap: 10px;
    flex-wrap: nowrap;
    min-width: 0;
    position: relative;
    z-index: 1;
  }
  .stat-value {
    font-family: ${FONT};
    font-weight: 700;
    font-size: 24px;
    line-height: 32px;
    color: ${(p) => p.$valueColor || FIG_INK};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    min-width: 0;
    flex: 0 1 auto;
  }
  /* A theme name is not a number: "General success/better future" clipped
     to "General succ..." beside its caption, which tells the reader nothing
     (user 2026-09-03). Text values wrap to a second line and shrink a
     little rather than truncating mid-word. */
  &.is-text .stat-value {
    font-size: 15px;
    line-height: 20px;
    white-space: normal;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    text-wrap: pretty;
  }
  /* With the value on two lines the caption drops below it instead of
     fighting for the same baseline. */
  &.is-text .stat-body { flex-wrap: wrap; align-items: baseline; }
  /* The caption sits INLINE beside the value on one baseline
     (Figma 7102:10706: "43  the full sample"), never on its own line. */
  .stat-sub {
    font-family: ${FONT};
    font-weight: 500;
    font-size: 12px;
    line-height: 18px;
    color: ${FIG_MUTED};
    flex: none;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 55%;
  }
  @media (max-width: 1280px) {
    padding: 18px 20px 14px;
    .stat-value { font-size: 20px; line-height: 26px; }
    &.is-text .stat-value { font-size: 14px; line-height: 19px; }
  }
`;

/* ── tabs ──────────────────────────────────────────────────────────── */

// Underline tabs (Figma 7102:10821). The v2 system only has the pill
// variant; the new frames use an underline for the in-page section tabs and
// keep pills for the Dashboard / Project Details switch above.
export const TabBar = styled.nav`
  display: flex;
  align-items: stretch;
  gap: 4px;
  border-bottom: 1px solid ${HAIRLINE};
  margin-bottom: 20px;
  overflow-x: auto;
  overscroll-behavior-x: contain;
  -webkit-overflow-scrolling: touch;
  &::-webkit-scrollbar { height: 0; }
`;

export const Tab = styled.button`
  appearance: none;
  background: none;
  border: none;
  border-bottom: 2px solid ${(p) => (p.$active ? PRIMARY : "transparent")};
  cursor: pointer;
  white-space: nowrap;
  padding: 10px 20px;
  font-family: ${FONT};
  font-size: 14px;
  line-height: 24px;
  @media (max-width: 1100px) { padding: 9px 14px; font-size: 13px; }
  @media (max-width: 700px) { padding: 8px 11px; font-size: 12.5px; }
  font-weight: ${(p) => (p.$active ? 600 : 500)};
  color: ${(p) => (p.$active ? PRIMARY : FIG_INK)};
  transition: color 140ms ease, border-color 140ms ease;
  &:hover { color: ${PRIMARY}; }
  &:focus-visible { outline: 2px solid ${PRIMARY}; outline-offset: -2px; }
`;

/* ── segmented control ─────────────────────────────────────────────── */

// Region | Income | Age | Employment | Education (Figma 7105:2495).
export const Segmented = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  background: #fff;
  border: 1px solid ${HAIRLINE};
  border-radius: 999px;
  padding: 3px;
  box-shadow: ${SHADOW_CTA};
  overflow-x: auto;
  max-width: 100%;
  &::-webkit-scrollbar { height: 0; }
`;

export const SegmentedItem = styled.button`
  @media (max-width: 1100px) { padding: 6px 12px; font-size: 12px; }
  @media (max-width: 700px) { padding: 6px 10px; font-size: 11.5px; }
  appearance: none;
  border: none;
  cursor: pointer;
  white-space: nowrap;
  border-radius: 999px;
  padding: 7px 16px;
  font-family: ${FONT};
  font-size: 13px;
  line-height: 16px;
  font-weight: ${(p) => (p.$active ? 600 : 500)};
  color: ${(p) => (p.$active ? "#fff" : FIG_INK)};
  background: ${(p) => (p.$active ? CTA_GRADIENT : "transparent")};
  transition: background 160ms ease, color 160ms ease;
  &:hover { color: ${(p) => (p.$active ? "#fff" : PRIMARY)}; }
  &:focus-visible { outline: 2px solid ${PRIMARY}; outline-offset: 2px; }
`;

/* ── bars ──────────────────────────────────────────────────────────── */

// One labelled bar row: label, track, value, count. The track is a plain
// element rather than a chart because these rows are read as a list, and a
// chart canvas cannot be selected, searched or read by a screen reader.
export const BarRow = styled.div`
  display: grid;
  grid-template-columns: ${(p) => p.$labelW || "200px"} minmax(0, 1fr) auto;
  align-items: center;
  gap: 14px;
  /* The row breathes 10px past its content on both sides so the hover halo
     never sits flush against the label - the Vital City fix (user 2026-08-24:
     "the hover border is getting sticky with label"). */
  margin: 0 -10px;
  padding: 7px 10px;
  font-family: ${FONT};
  font-size: 12.5px;
  color: ${FIG_INK};

  ${(p) =>
    p.$clickable &&
    css`
      cursor: pointer;
      border-radius: 10px;
      transition: background 150ms ease;
      &:hover { background: #eef0fc; }
      &:focus-visible {
        outline: 2px solid rgba(46, 69, 218, 0.55);
        outline-offset: -2px;
      }
    `}

  @media (max-width: 767.99px) {
    grid-template-columns: 1fr auto;
    row-gap: 4px;
    .bar-track { grid-column: 1 / -1; }
  }
`;

export const BarLabel = styled.span`
  font-weight: 600;
  color: ${(p) => (p.$muted ? FIG_MUTED : FIG_INK)};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const BarTrack = styled.span.attrs({ className: "bar-track" })`
  display: block;
  position: relative;
  height: ${(p) => p.$h || 20}px;
  border-radius: 999px;
  background: ${TRACK};
  overflow: hidden;
`;

export const BarValue = styled.span`
  display: inline-flex;
  align-items: baseline;
  gap: 8px;
  justify-content: flex-end;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
  /* BOTH sub-columns are fixed width. Every BarRow is its own grid, so an
     auto-sized value column sizes to THAT row's digits and a 2-digit row
     lands 9px off a 3-digit one (measured 1441 vs 1450). Fixing the count
     box AND the unit box makes the whole value block a constant width, so
     the grid column matches across rows and both edges line up. */
  b {
    font-weight: 600;
    color: ${FIG_INK};
    min-width: 3.4ch;
    text-align: right;
  }
  small {
    font-size: 13px;
    font-weight: 500;
    color: ${FIG_MUTED};
    min-width: ${(p) => p.$unitW || "3.8ch"};
    text-align: left;
  }
`;

// A single row of coloured segments summing to the whole (Figma 7102:10901).
export const StackedBar = styled.div`
  display: flex;
  /* 48 read as a slab beside the 20px bar rows (user 2026-08-24), and 34 was
     still heavy once several stack under one question. */
  height: ${(p) => p.$h || 28}px;
  border-radius: ${RADIUS_CTA}px;
  overflow: hidden;
  background: ${TRACK};
`;

export const StackedSegment = styled.button`
  appearance: none;
  border: none;
  padding: 0;
  cursor: ${(p) => (p.$clickable ? "pointer" : "default")};
  flex: 0 0 ${(p) => p.$pct}%;
  background: ${(p) => p.$color};
  color: #fff;
  font-family: ${FONT};
  font-size: 16px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: filter 140ms ease;
  &:hover { filter: ${(p) => (p.$clickable ? "brightness(1.08)" : "none")}; }
  &:focus-visible { outline: 2px solid ${PRIMARY}; outline-offset: -3px; }
`;

/* ── legend ────────────────────────────────────────────────────────── */

export const LegendRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px 24px;
  margin-top: 16px;
`;

export const LegendItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-family: ${FONT};
  /* A step down from the frame's 14/18: with four or five entries under every
     bar the legend was competing with the data (user 2026-08-24). */
  font-size: 12.5px;
  font-weight: 500;
  color: ${FIG_MUTED};
  &::before {
    content: "";
    width: 14px;
    height: 14px;
    border-radius: 4px;
    background: ${(p) => p.$color};
    flex: none;
  }
`;

/* ── misc ──────────────────────────────────────────────────────────── */

export const Empty = styled.p`
  font-family: ${FONT};
  font-size: 14px;
  line-height: 22px;
  color: ${FIG_MUTED};
  margin: 0;
  padding: 12px 0;
`;
